import Axios, { AxiosError, CancelTokenSource } from 'axios';
import Redirect301OnTheFlyForm from 'features/redirects-301/Redirect301OnTheFlyForm';
import arrayMutators from 'final-form-arrays';
import * as React from 'react';
import { Field, Form } from 'react-final-form';
import { Omit } from 'react-router';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
	Alert,
	Button,
	ButtonGroup,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
} from 'reactstrap';
import { Direction } from 'reactstrap/lib/Dropdown';
import AxiosHelper from 'services/AxiosHelper';
import { HistoryHelper } from 'services/HistoryHelper';
import { ModelStatus } from 'shared-client/types/model-status';
import { PERMANENT_ENTITY_DELETED_ERROR_MESSAGE } from 'shared-client/util/Constants';
import { EntityVersionType, RedirectData } from 'types';
import DeleteCommandBase from 'types/DeleteCommandBase';
import { RemovalErrorCode } from 'types/ExceptionCodes';
import { RemovalException } from 'types/Exceptions';
import { GetScopedSites } from 'util/AppStateUtils';
import { handleAxiosErrorWithValidation } from 'util/Misc';
import Confirm from '../Confirm';
import Spinner from '../Spinner';
import GarbageCanSVG from '../svg/GarbageCanSVG';
import { toastSuccess } from '../toast/Toasts';
import { areRedirectsEqual } from '../Utils';
import EditErrorAlertPopup from './EditErrorAlertPopup';
import { DateTime } from './fields';
import withEditContext, { HasEditContext } from './withEditContext';
import RocTitle from '../RocTitle';
import ValidationErrorAlert, { AlertType } from '~/components/shared/edit/ValidationErrorAlert';
import AutoSuggestSingleField from '~/components/shared/edit/fields/AutoSuggestSingleField';
import { AutoSuggestItem } from '~/components/shared/auto-suggest/Types';

enum ModalStatus {
	/** No popups are being displayed */
	Closed = 0,
	/** The deletion confirmation popup is being displayed */
	DeletePopup,
	/** The schedule deletion popup is being displayed */
	SchedulePopup,
	/** The error or force delete confirmation popup is being displayed */
	ErrorPopup,
}

interface EditDeleteButtonProps extends RouteComponentProps<any>, HasEditContext {
	/**
	 * Controls whether or not this component is disabled.
	 */
	disabled: boolean;
	/**
	 * The direction of the dropdown menu
	 */
	dropdownMenuDirection?: Direction;
	/**
	 * Did an error occur during form submission?
	 */
	submitError: string | string[] | null;
	/**
	 * If set to true, this entity is flagged as a permanent entity, and therefore it cannot be deleted.
	 */
	isPermanent?: boolean;

	/**
	 * If set to false, this entity cannot be deleted.
	 */
	isDeletable?: boolean;
}

interface EditDeleteButtonState {
	/**
	 * Indicates which modal is open (if any).
	 */
	modalStatus: ModalStatus;
	/**
	 * Is the delete options modal open?
	 */
	dropdownOpen: boolean;
	/**
	 * Is the entity currently being deleted?
	 */
	deleting: boolean;
	/**
	 * The exception (if any) that occurred when attempting to delete the entity.
	 */
	axiosError: AxiosError | null;

	/**
	 * A list of site names that should match the `redirects` field array.
	 * This is used to display the label for each 301 a user might create when the
	 * deletion modal is open.
	 */
	siteNamesFor301s: string[];

	/**
	 * The initial form values to populate the deletion modal with.
	 */
	initialRedirectValues: RedirectData[];

	/**
	 * The redirects that will be provided during the deletion when the user confirms that they wish
	 * to force deletion of an entity with published related data.
	 */
	redirectsWhenForcing?: RedirectData[];

	/**
	 * Whether or not to display the validation error for the override dropdown field.
	 */
	showValidationError?: boolean;

	/**
	 * Stores the selected entity definition from the override dropdown field.
	 */
	selectedEntityDefinition?: AutoSuggestItem | null;
}

/**
 * Component for deleting entities from within the entity edit page
 *
 * @class EditDeleteButton
 * @extends {React.Component<EditDeleteButtonProps, EditDeleteButtonState>}
 */
class EditDeleteButton extends React.Component<EditDeleteButtonProps, EditDeleteButtonState> {
	private requestToken: CancelTokenSource;
	private containerRef;

	constructor(props) {
		super(props);

		this.state = {
			dropdownOpen: false,
			modalStatus: ModalStatus.Closed,
			deleting: false,
			axiosError: null,
			siteNamesFor301s: [],
			initialRedirectValues: [],
			showValidationError: false,
			selectedEntityDefinition: null,
		};

		this.requestToken = Axios.CancelToken.source();
		this.containerRef = React.createRef();
	}

	public componentWillUnmount() {
		// when the component is being destroyed, cancel any inflight delete requests
		this.requestToken.cancel();
	}

	/**
	 * Toggles the visibility of the delete options dropdown.
	 */
	private toggleDropdown = () => {
		this.setState((prevState) => ({
			dropdownOpen: !prevState.dropdownOpen,
		}));
	};

	/**
	 * Sets which modal is being displayed.
	 */
	private setModal = (modal: ModalStatus) => {
		this.setState({
			modalStatus: modal,
			dropdownOpen: false,
		});
	};

	/**
	 * Toggles the visibility of the modal. Usually called to hide it.
	 */
	private toggleModal = (modal: ModalStatus) => {
		const newModal = this.state.modalStatus === modal ? ModalStatus.Closed : modal;
		this.setState({
			modalStatus: newModal,
			dropdownOpen: false,
		});
	};

	/**
	 * Called when the delete button is clicked. Displays the modal for a user to confirm that they want to delete this
	 * entity record.
	 */
	private attemptDelete = () => {
		const {
			editContext: { originalScope },
		} = this.props;

		const sites = GetScopedSites(originalScope);

		const initialFormValues = sites.map((site) => ({
			redirectUrl: '',
			siteId: site.id,
		}));

		this.setState({
			siteNamesFor301s: sites.map((site) => site.name),
			initialRedirectValues: initialFormValues,
		});

		// now display the deletion modal
		this.setModal(ModalStatus.DeletePopup);
	};

	/**
	 * Called when the delete confirmation button is submitted. Handles deleting entities from edit pages.
	 */
	private onConfirmDelete = async (formValues: any): Promise<void | Record<string, unknown>> => {
		// when we initially confirm the deletion, use the redirect values from the form
		return this.requestDeleteAsync(
			/* force */ false,
			formValues && formValues.redirects,
			formValues && formValues.dropdownOverride,
		);
	};

	/**
	 * Called when the user clicks the confirmation button that they want to still delete the record, even though it's going to touch other records.
	 */
	private onConfirmForceDelete = async (): Promise<void | Record<string, unknown>> => {
		// when confirming a forced deletion, the redirects were saved in state since there's no form anymore
		return this.requestDeleteAsync(/* force */ true, this.state.redirectsWhenForcing);
	};

	private requestDeleteAsync = async (
		force: boolean,
		redirects?: RedirectData[],
		dropdownOverride?: AutoSuggestItem,
	): Promise<void | Record<string, unknown>> => {
		const {
			editContext: {
				entityDefinition: { baseAjaxRoute },
				values,
			},
			editContext,
		} = this.props;

		const url = `${baseAjaxRoute}/${values.id}`;

		const data: DeleteCommandBase = {
			force,
			redirects,
			dropdownOverride,
		};

		const historyId = this.props.editContext.getActiveVersionId();

		if (historyId) {
			data.historyId = historyId;
		}

		try {
			this.setState({ deleting: true });

			await AxiosHelper.delete(url, { data, cancelToken: this.requestToken.token });

			// Display a toast so users know the deletion was successful
			toastSuccess({
				toastifyOptions: {
					position: toast.POSITION.BOTTOM_RIGHT,
				},
				customContent: <>{editContext.entityDefinition.singularName} successfully deleted.</>,
			});

			// redirect back to the listing page
			editContext.redirectToLastLocation();
		} catch (error) {
			if (Axios.isCancel(error)) {
				return;
			}

			const axiosError: AxiosError = error;
			const serverException: RemovalException | undefined = axiosError.response && axiosError.response.data;

			if (serverException && serverException.exceptionCode === RemovalErrorCode) {
				// if we get here, the error was due to illegal removal of a record with published related records
				this.setState({
					modalStatus: ModalStatus.ErrorPopup,
					axiosError: axiosError,
					// save off the entered redirects into state for when the user confirms a forced deletion
					redirectsWhenForcing: data.redirects,
				});

				return;
			}

			// an error occurred that wasn't due to an illegal removal (could have been validation or an unexpected error)
			if (axiosError && axiosError.response && axiosError.response.status === 400 && axiosError.response.data) {
				// if a validation error happened, bind it to the form
				return handleAxiosErrorWithValidation(axiosError, 'An error occurred while deleting.');
			}

			// otherwise, just an unexpected error occurred
			console.error('Unable to delete:', axiosError.message);
		} finally {
			this.setState({
				deleting: false,
			});
		}
	};

	/**
	 * Called when the scheduled deletion modal is submitted.
	 */
	private continueWithScheduledDelete = async (formValues: any): Promise<void | Record<string, unknown>> => {
		const data = { ...formValues };

		const {
			editContext: {
				entityDefinition: { baseAjaxRoute },
				values,
			},
			editContext,
		} = this.props;

		const historyId = this.props.editContext.getActiveVersionId();

		if (historyId) {
			data.historyId = historyId;
		}

		const url = `${baseAjaxRoute}/${values.id}/schedule`;

		try {
			this.setState({ deleting: true });

			await AxiosHelper.delete(url, { data, cancelToken: this.requestToken.token });
		} catch (error) {
			if (Axios.isCancel(error)) {
				return;
			}

			this.setState({ deleting: false });

			// bind any backend validation errors back into the scheduled deletion form
			return handleAxiosErrorWithValidation(error, 'An error occurred while submitting deletion');
		}

		// Display a toast so users know the scheduled deletion was successful
		toastSuccess({
			toastifyOptions: {
				position: toast.POSITION.BOTTOM_RIGHT,
			},
			customContent: (
				<>
					{editContext.entityDefinition.singularName} deletion successfully scheduled for{' '}
					{data.scheduledTime.format('MMMM Do, YYYY')} at {data.scheduledTime.format('h:mm a')}.
				</>
			),
		});

		// redirect back to the listing page
		// TODO: for a future improvement, this could potentially redirect to the edit
		// page for the future version of the entity that was just deleted
		editContext.redirectToLastLocation();
	};

	/**
	 * Should the delete button be entirely hidden?
	 */
	private isDeleteHidden = (): boolean => {
		const {
			editContext: { values, isCreateMode },
		} = this.props;

		if (isCreateMode) {
			// if we're in create mode, there's nothing to delete
			return true;
		}

		const activeVersion = this.props.editContext.getActiveVersion();

		if (activeVersion !== null) {
			// the active version of the entity we're looking at isn't the live version

			if (activeVersion.type === EntityVersionType.Archived) {
				// don't display the delete button if we're looking at older history record that was archived
				return true;
			}
		} else {
			// if the version is null, we're looking at the current version of the entity

			if (values && values.status === ModelStatus.Deleted) {
				// the current entity is deleted, can't re-delete
				return true;
			}
		}

		return false;
	};

	/**
	 * Should the delete options be entirely hidden?
	 */
	private areDeleteOptionsHidden = (): boolean => {
		if (this.isDeleteHidden()) {
			// if the delete button is entirely hidden, the options should be hidden too
			return true;
		}

		const {
			editContext: { entityDefinition },
		} = this.props;

		if (entityDefinition.hideDeleteOptions) {
			// explicitly configured to hide the delete options
			return true;
		}

		if (!entityDefinition.canDeletionBeScheduled) {
			// if deletion can't be scheduled, don't display deletion options as scheduling is currently
			// the only option
			return true;
		}

		const activeVersion = this.props.editContext.getActiveVersion();

		if (activeVersion !== null) {
			// the active version of the entity we're looking at isn't the live version
			if (activeVersion.type === EntityVersionType.Future) {
				return false;
			}

			return true;
		}

		return false;
	};

	/**
	 * Handles the change to the selected entity definition from the autosuggest
	 *
	 * @memberof EditDeleteButton
	 */
	private handleEntityDefinitionSelection = (autoSuggestValue) => {
		this.setState({
			showValidationError: !autoSuggestValue,
			selectedEntityDefinition: autoSuggestValue,
		});
	};

	/**
	 * The good old trusty React render method
	 *
	 * @returns
	 * @memberof EditDeleteButton
	 */
	public render() {
		const {
			editContext: { entityDefinition, values },
			editContext,
			disabled,
			dropdownMenuDirection = 'down',
			isPermanent = false,
			isDeletable = true,
		} = this.props;

		const { dropdownOpen, modalStatus, deleting, axiosError, showValidationError } = this.state;

		let recordName: string | null = null;

		if (values) {
			recordName = ` (${values.name ? values.name : values[entityDefinition.userFriendlyNameField]})`;
		}

		if (this.isDeleteHidden()) {
			return null;
		}

		const activeVersion = editContext.getActiveVersion();
		const hideDeleteOptions = this.areDeleteOptionsHidden();

		return (
			<div className="edit-form__actions-bar-item">
				<RocTitle label={PERMANENT_ENTITY_DELETED_ERROR_MESSAGE} isEnabled={isPermanent} alignment="left">
					<>
						<ButtonGroup>
							<Button
								color="danger"
								className="btn-medium"
								onClick={this.attemptDelete}
								disabled={disabled || isPermanent || !isDeletable}
								data-testid="delete-button"
							>
								<div className="btn-icon-text-container">
									<span className="btn-icon-bg">
										<GarbageCanSVG />
									</span>
									<span className="btn-icon-text">Delete</span>
								</div>
							</Button>

							{!hideDeleteOptions && !isPermanent ? (
								<>
									<Dropdown
										isOpen={dropdownOpen}
										toggle={this.toggleDropdown}
										className="dropdown--right"
										direction={dropdownMenuDirection}
									>
										<DropdownToggle
											color="danger"
											caret
											className="dropdown-toggle-no-text dropdown-toggle"
											disabled={disabled}
											data-testid="delete-dropdown-toggle"
										>
											<span className="visually-hidden">
												{dropdownOpen ? 'Close' : 'Open'} Delete Options
											</span>
										</DropdownToggle>
										<DropdownMenu>
											<DropdownItem
												onClick={() => this.setModal(ModalStatus.SchedulePopup)}
												data-testid="delete-dropdown-delete-at"
											>
												Delete At
											</DropdownItem>
										</DropdownMenu>
									</Dropdown>
								</>
							) : null}
						</ButtonGroup>

						<Form
							mutators={{ ...arrayMutators }}
							onSubmit={this.onConfirmDelete}
							render={(props) => {
								const { handleSubmit, submitError } = props;

								const onConfirm = () => {
									if (
										!entityDefinition.showOverrideDropdown ||
										!entityDefinition.validateOverrideValue
									) {
										handleSubmit();
									}

									if (this.state.selectedEntityDefinition) {
										this.setState({ showValidationError: false });
										handleSubmit();
									} else {
										this.setState({ showValidationError: true });
									}
								};

								return (
									<Field name="redirects" subscription={{ dirty: true }} isEqual={areRedirectsEqual}>
										{(props) => {
											const {
												meta: { dirty },
											} = props;

											const show301Form =
												// show the 301 creation form if not hidden and the entity has a url
												!editContext.entityDefinition.hideCreate301OnTheFly &&
												values &&
												values.status === ModelStatus.Published &&
												values.url;

											return (
												<Confirm
													color="danger"
													isOpen={modalStatus === ModalStatus.DeletePopup}
													onCancel={() => {
														this.setModal(ModalStatus.Closed);
														this.setState({ showValidationError: false });
													}}
													onConfirm={onConfirm}
													renderTitle={() => 'Confirm Deletion'}
													confirmButtonText={
														dirty || activeVersion !== null || !show301Form
															? // display just "delete" if a 301 has been entered, if we're editing a non-current version, or if the 301 form is hidden
															  'Delete'
															: // otherwise show the skip option since the 301 form is visible and is clean
															  'Skip & Delete'
													}
													confirmTestId="button-confirm-delete"
													cancelTestId="button-cancel-delete"
													inProgress={deleting}
													renderContents={() => {
														return activeVersion === null ? (
															<>
																{submitError ? (
																	<ValidationErrorAlert
																		submitError={submitError}
																		alertType={AlertType.Delete}
																	/>
																) : null}
																{/* if the current version is null, we're looking at the current record */}
																<h4>
																	Are you sure you want to delete this{' '}
																	{editContext.entityDefinition.singularName}
																	{recordName}?
																</h4>

																{show301Form ? (
																	<>
																		<p>
																			If you would like to create site specific
																			301s from the URL of the item, to a new URL,
																			please enter them below.
																		</p>

																		<Redirect301OnTheFlyForm
																			record={values}
																			initialValue={
																				this.state.initialRedirectValues
																			}
																			siteNames={this.state.siteNamesFor301s}
																			submitError={submitError}
																			fieldName="redirects"
																		/>
																	</>
																) : null}

																{entityDefinition.showOverrideDropdown ? (
																	<>
																		<p>
																			If you would like to override all uses of
																			this <b>{entityDefinition.singularName}</b>{' '}
																			please choose a new value from the dropdown
																			below.
																		</p>

																		<AutoSuggestSingleField
																			label={entityDefinition.singularName}
																			name="dropdownOverride"
																			onChange={
																				this.handleEntityDefinitionSelection
																			}
																			autoSuggestOptions={{
																				baseUrl: `${entityDefinition.baseAjaxRoute}`,
																				expandOnClick: true,
																				params: {
																					ignoredIds: [values.id],
																				},
																			}}
																			isRequired={
																				entityDefinition.validateOverrideValue
																			}
																		/>
																		{showValidationError &&
																		entityDefinition.validateOverrideValue ? (
																			<Alert color="danger">
																				An {entityDefinition.singularName} must
																				be selected.
																			</Alert>
																		) : null}
																	</>
																) : null}
															</>
														) : (
															// otherwise, we're looking at the non-current record
															<>
																Are you sure you want to delete this{' '}
																{HistoryHelper.getVersionTypeName(activeVersion)}{' '}
																{editContext.entityDefinition.singularName}
																{recordName}?
															</>
														);
													}}
												/>
											);
										}}
									</Field>
								);
							}}
						/>

						<EditErrorAlertPopup
							axiosError={axiosError}
							isOpen={modalStatus === ModalStatus.ErrorPopup}
							onCancel={() => this.setModal(ModalStatus.Closed)}
							onConfirm={this.onConfirmForceDelete}
							inProgress={deleting}
						/>

						<Modal
							isOpen={modalStatus === ModalStatus.SchedulePopup}
							toggle={() => this.toggleModal(ModalStatus.SchedulePopup)}
							className="edit-save-delete-modal modal-lg"
						>
							<Form
								onSubmit={this.continueWithScheduledDelete}
								render={(props) => {
									const { handleSubmit, submitError, submitting } = props;

									return (
										<form onSubmit={handleSubmit}>
											<ModalHeader toggle={() => this.toggleModal(ModalStatus.SchedulePopup)}>
												Select Deletion Date
											</ModalHeader>
											<ModalBody>
												<span>
													When would you like to schedule the deletion of this{' '}
													{editContext.entityDefinition.singularName}
													{recordName}?
												</span>
												<ValidationErrorAlert
													submitError={submitError}
													alertType={AlertType.Edit}
												/>
												<span className="visually-hidden">Scheduled deletion time</span>
												<Field name="scheduledTime" component={DateTime} />
											</ModalBody>
											<ModalFooter>
												{deleting ? <Spinner /> : null}
												<Button
													className="btn-outline-primary-alt btn-width-large"
													outline
													onClick={() => this.toggleModal(ModalStatus.SchedulePopup)}
												>
													Cancel
												</Button>{' '}
												<Button
													type="submit"
													color="primary"
													className="btn-width-large"
													disabled={submitting}
													data-testid="submit-scheduled-date"
												>
													OK
												</Button>
											</ModalFooter>
										</form>
									);
								}}
							/>
						</Modal>
					</>
				</RocTitle>
			</div>
		);
	}
}

export class EditDeleteButtonWrapped extends EditDeleteButton {}

export default withRouter<Omit<EditDeleteButtonProps, keyof HasEditContext>, any>(
	withEditContext(EditDeleteButtonWrapped),
);
